import type { CSSProperties } from 'react';
import { formatRating, type TierId, tierById, tierFor, UNRATED_FILL } from '#shared/core/ratings';

/**
 * Rating display helpers. The scale is 0 to 99 and unrated shows ?? (formatRating from
 * shared/src/core/ratings.ts is the only formatter). Single digits are allowed: a 7 is a 7.
 */

export { formatRating };

/** "??" for unrated, else the clamped number. */
export function ratingText(v: number | null | undefined): string {
  return formatRating(v);
}

/** The tier for an OVR, or null when unrated. */
export function tierOf(ovr: number | null | undefined): TierId | null {
  return ovr === null || ovr === undefined || Number.isNaN(ovr) ? null : tierFor(ovr);
}

export function tierName(tier: TierId | null): string {
  return tier ? tierById(tier).name : 'Unrated';
}

/** Hex fill for a tier (pastel ramp), or the pale lilac for unrated. */
export function tierFill(tier: TierId | null): string {
  return tier ? tierById(tier).fill : UNRATED_FILL;
}

/**
 * Inline style that sets --tier-fill for a tiered component. Use this instead of building
 * class names at runtime (Tailwind only sees class names written out in source).
 */
export function tierStyle(tier: TierId | null): CSSProperties {
  return { '--tier-fill': tierFill(tier) } as CSSProperties;
}

/** "+2", "-1", "0" with a real minus sign, for delta pills (always paired with an arrow). */
export function formatDelta(d: number): string {
  if (d > 0) return `+${d}`;
  if (d < 0) return `−${Math.abs(d)}`;
  return '0';
}
